import React, { useMemo, useImperativeHandle, forwardRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface Feedback {
  id: number;
  user_id?: number;
  email?: string;
  name?: string;
  subject?: string;
  message: string;
  category: string;
  status: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

interface FeedbackGridProps {
  feedback: Feedback[];
  searchValue?: string;
  onUpdateStatus?: (feedbackId: number, status: string) => void;
}

const FeedbackGrid = forwardRef<any, FeedbackGridProps>(
  ({ feedback, searchValue = '', onUpdateStatus }, ref) => {
    const { colors } = useTheme();
    const gridRef = React.useRef<AgGridReact>(null);
    const gridApiRef = React.useRef<any>(null);

    // Only render on web
    if (Platform.OS !== 'web') {
      return null;
    }

    // Filter feedback based on search value
    const filteredFeedback = React.useMemo(() => {
      if (!searchValue.trim()) return feedback;
      const lowerSearch = searchValue.toLowerCase();
      return feedback.filter((fb) => {
        const searchFields = [
          fb.email,
          fb.name,
          fb.subject,
          fb.message,
          fb.status,
          fb.category,
          fb.id?.toString(),
        ];
        return searchFields.some(
          (field) => field && String(field).toLowerCase().includes(lowerSearch)
        );
      });
    }, [feedback, searchValue]);

    const columnDefs: ColDef[] = useMemo(
      () => [
        { field: 'id', headerName: 'ID', width: 80, sortable: true },
        {
          field: 'email',
          headerName: 'Email',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'name',
          headerName: 'Name',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'subject',
          headerName: 'Subject',
          flex: 1,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            return params.value || 'No Subject';
          },
        },
        {
          field: 'message',
          headerName: 'Message',
          flex: 2,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const message = params.value || '';
            return message.length > 50
              ? `${message.substring(0, 50)}...`
              : message;
          },
        },
        {
          field: 'category',
          headerName: 'Category',
          width: 120,
          sortable: true,
          filter: true,
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 120,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const status = params.value || 'new';
            const statusConfig: {
              [key: string]: { label: string; color: string };
            } = {
              reviewing: { label: 'Reviewing', color: palette.brightBlue },
              in_progress: { label: 'In Progress', color: palette.lightOrange },
              done: { label: 'Done', color: palette.cyanLimeGreen },
              no_action_needed: {
                label: 'No Action',
                color: palette.darkGrayBlue,
              },
              new: { label: 'New', color: palette.brightViolet },
              read: { label: 'Read', color: palette.softBlue },
              resolved: { label: 'Resolved', color: palette.cyanLimeGreen },
            };

            const config = statusConfig[status] || {
              label: status,
              color: palette.darkGrayBlue,
            };

            return (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: `${config.color}20`,
                  color: config.color,
                  border: `1px solid ${config.color}40`,
                  fontSize: '11px',
                  fontWeight: '600',
                  width: 'fit-content',
                }}
              >
                {config.label}
              </div>
            );
          },
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 180,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          field: 'updated_at',
          headerName: 'Updated',
          width: 180,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          field: 'completed_at',
          headerName: 'Completed',
          width: 180,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '-';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          headerName: 'Actions',
          width: 400,
          cellRenderer: (params: any) => {
            const feedbackItem = params.data;
            const currentStatus = feedbackItem.status || 'new';
            const statusOptions = [
              {
                value: 'reviewing',
                label: 'Reviewing',
                color: palette.brightBlue,
              },
              {
                value: 'in_progress',
                label: 'In Progress',
                color: palette.lightOrange,
              },
              { value: 'done', label: 'Done', color: palette.cyanLimeGreen },
              {
                value: 'no_action_needed',
                label: 'No Action',
                color: palette.darkGrayBlue,
              },
            ];

            return (
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  height: '100%',
                  flexWrap: 'wrap',
                }}
              >
                {statusOptions.map((option) => {
                  const isActive = currentStatus === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() =>
                        onUpdateStatus &&
                        onUpdateStatus(feedbackItem.id, option.value)
                      }
                      style={{
                        padding: '4px 8px',
                        backgroundColor: isActive
                          ? option.color
                          : `${option.color}20`,
                        color: isActive ? 'white' : option.color,
                        border: `1px solid ${option.color}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '10px',
                        fontWeight: isActive ? '600' : '400',
                        opacity: isActive ? 1 : 0.8,
                      }}
                      title={option.label}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            );
          },
        },
      ],
      [onUpdateStatus]
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: 'legacy',
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
        },
        pagination: false,
        rowHeight: 50,
        headerHeight: 40,
        suppressCellFocus: true,
        animateRows: true,
        suppressPaginationPanel: true,
        onGridReady: (params) => {
          gridApiRef.current = params.api;
        },
      }),
      []
    );

    // No need for useEffect - filtering is done via filteredFeedback

    useImperativeHandle(ref, () => ({
      getApi: () => gridRef.current?.api,
    }));

    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <div
          className="ag-theme-alpine"
          style={
            {
              height: 'calc(100vh - 200px)',
              width: '100%',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              '--ag-header-background-color': colors.card,
              '--ag-background-color': colors.background,
              '--ag-odd-row-background-color': colors.card,
              '--ag-row-hover-color': colors.card,
              '--ag-header-foreground-color': colors.text,
              '--ag-foreground-color': colors.text,
              '--ag-border-color': colors.border,
              '--ag-secondary-border-color': colors.border,
              '--ag-input-focus-border-color': colors.primary,
              '--ag-selected-row-background-color': `${colors.primary}20`,
              '--ag-range-selection-background-color': `${colors.primary}15`,
              '--ag-range-selection-border-color': colors.primary,
              '--ag-checkbox-checked-color': colors.primary,
              '--ag-checkbox-unchecked-color': colors.border,
              '--ag-input-disabled-background-color': colors.card,
              '--ag-input-disabled-border-color': colors.border,
              '--ag-input-disabled-foreground-color': colors.secondary,
              '--ag-panel-background-color': colors.card,
              '--ag-tool-panel-background-color': colors.card,
              '--ag-side-button-selected-background-color': colors.primary,
              '--ag-side-button-selected-foreground-color': palette.white,
            } as React.CSSProperties & { [key: string]: any }
          }
        >
          <style>{`
          .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
          .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
            display: none;
          }
          .ag-theme-alpine .ag-body-viewport,
          .ag-theme-alpine .ag-body-horizontal-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .ag-theme-alpine {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-size: 12px;
          }
          .ag-theme-alpine .ag-header {
            border-bottom: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell {
            border-right: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell-text {
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-row {
            border-bottom: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine .ag-cell {
            border-right: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
          }
          .ag-theme-alpine .ag-cell-value {
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine input,
          .ag-theme-alpine select,
          .ag-theme-alpine button {
            font-size: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
        `}</style>
          <AgGridReact
            ref={gridRef}
            rowData={filteredFeedback}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            domLayout="normal"
          />
        </div>
      </View>
    );
  }
);

FeedbackGrid.displayName = 'FeedbackGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
});

export default FeedbackGrid;
